import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SampleConstants from 'constants/SampleConstants';
import {formatGridCoordinates, isGrid} from 'samples/SampleUtil';
import * as ParentStorableTypes from 'samples/storables/ParentStorableTypes';
import * as ParentStorableUtil from 'samples/storables/ParentStorableUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {getCreator} from 'ui-library/inputs/ItemOptionContent';
import APIUtil from 'util/APIUtil';
import {listToTruncatedReadable} from 'util/NameUtil';

function ancestralStorablesDisplayString(extra: ParentStorableTypes.StorableSearchResultExtra): string {
  let ancestralStorablesDisplayString = ParentStorableUtil.getAncestralStorablesDisplayString(
    extra.ancestralStorables
  );
  if (extra.gridCell) {
    ancestralStorablesDisplayString = [
      ancestralStorablesDisplayString,
      formatGridCoordinates(extra.gridCell.rowIndex, extra.gridCell.columnIndex),
    ].join(' / ');
  }
  return ancestralStorablesDisplayString;
}

function containedItemsToDisplayString(containedItems: Array<SampleConstants.ContainableSummary>): string {
  if (containedItems.length === 0) {
    return 'No contents';
  } else {
    return `${listToTruncatedReadable(containedItems.map((item) => item.name))}`;
  }
}

interface StorableDetailsDisplayProps {
  searchResult: ParentStorableTypes.StorableSearchResult;
  /** Whether to render capacity with error styling */
  shouldCallOutCapacity?: boolean;
  /** Whether to include capacity info for locations - leveraged to show this only when moving plates/boxes/containers */
  includeLocationCapacityInfo?: boolean;
}

export default function StorableDetailsDisplay(props: StorableDetailsDisplayProps): JSX.Element {
  const {searchResult} = props;
  let detailString = '';
  const creator = getCreator(searchResult);
  const projectName = searchResult.source ? searchResult.source.name : null;
  if (projectName) {
    detailString = creator ? `${projectName} \u00b7 Created by ${creator}` : `${projectName}`;
  } else {
    detailString = `Created by ${creator}`;
  }
  const storable = searchResult.item;
  const isGridWithCapacityInfo =
    isGrid(storable.id) && searchResult.extra && searchResult.extra.childContainerStatus;
  const isLocationWithCapacityInfo =
    APIUtil.isLocation(storable.id) &&
    searchResult.extra &&
    searchResult.extra.locationCapacityInfo &&
    props.includeLocationCapacityInfo;
  return (
    <div className="u-lr-split">
      {searchResult.extra ? (
        <span className="u-ellipsis-text">{ancestralStorablesDisplayString(searchResult.extra)} </span>
      ) : (
        <span>{detailString}</span>
      )}
      <span className="u-flex-none">
        {(isGridWithCapacityInfo || isLocationWithCapacityInfo) && (
          <span className={props.shouldCallOutCapacity ? css(styles.lightRed) : undefined}>
            {ParentStorableUtil.getParentCapacityStatusDisplayString(
              searchResult.item,
              (searchResult.extra?.childContainerStatus ?? searchResult.extra?.locationCapacityInfo)!
            )}
          </span>
        )}
        {searchResult.extra && searchResult.extra.containedItems && (
          <span>
            <BenchlingIcon className="u-mini-spacing-right" icon={ModelTypeConstants.BATCH} />
            {containedItemsToDisplayString(searchResult.extra.containedItems)}
          </span>
        )}
      </span>
    </div>
  );
}

export const styles = StyleSheet.create({
  lightRed: {
    color: Color.RED_400,
  },
});
